import { useChatStoreActions } from "PFApp/stores/chat_store";
import { usePusherEvent } from "PFCore/base/pusher";
import { gidFor, gidToType } from "PFCore/helpers/gid";
import { useOnceWhen } from "PFCore/helpers/use_once_when";
import { useChatConversations } from "PFCore/hooks/queries/chat/use_chat_conversations";
import { useCurrentProfile } from "PFCore/hooks/queries/profile/use_current_profile";
import { fetchParticipants } from "PFCore/services/chat/fetch_participants";
import { Activity, GId, GIdTargetId, GIdTargetType } from "PFTypes";
import { ChatParticipant } from "PFTypes/chat_participant";
import { useCallback, useEffect, useMemo, useState } from "react";

export const useActivityPageConversations = (activity: Activity) => {
  const { data: currentProfile } = useCurrentProfile();
  const { renderChat, unrenderChat } = useChatStoreActions();

  const { data: { entries: conversations } = {}, refetch } = useChatConversations(
    {
      target: gidFor(GIdTargetType.Task, activity.id),
      perPage: 1000
    },
    {}
  );

  const refetchChatConversations = useCallback(
    ({ resource_gid, target_id }: { resource_gid: GId; target_id: GIdTargetId }) => {
      const type = gidToType(resource_gid);
      if (
        ["Chat::Conversation", "Task"].includes(type) &&
        parseInt(String(target_id)) === parseInt(String(activity.id))
      ) {
        refetch();
      }
    },
    [refetch, activity.id]
  );

  usePusherEvent(`private-profile-${currentProfile.id}`, "create", refetchChatConversations);
  usePusherEvent(`private-profile-${currentProfile.id}`, "change", refetchChatConversations);

  const primaryConversation = conversations?.find((conversation) => conversation.kind === "primary");
  const otherConversations = useMemo(
    () =>
      conversations?.filter(
        ({ id, createdAt, lastActivityAt }) => id !== primaryConversation?.id && createdAt !== lastActivityAt // don't show the empty ones
      ),
    [conversations, primaryConversation]
  );

  useOnceWhen(
    !!primaryConversation,
    () => {
      if (activity.profile.id !== currentProfile.id) {
        const { id } = primaryConversation!;
        renderChat(id, { minimized: true, expanded: true });
      }
    },
    () => {
      primaryConversation && unrenderChat(primaryConversation.id);
    }
  );

  const [participantsOfConversations, setParticipantsOfConversations] = useState<
    Record<string, ChatParticipant[]>
  >({});

  useEffect(() => {
    if (conversations) {
      const arrOfConversationParticipants = conversations.map((conversation) =>
        fetchParticipants({
          conversationId: conversation.id,
          withMessage: conversation.kind === "primary" && !activity.private // main is special (in ActivityDetailsMainChat). If is private we send the whole participant list
        })
      );

      Promise.all(arrOfConversationParticipants).then((conversationsParticipants) => {
        const participantsOfConversations = conversationsParticipants.reduce(
          (acc, { entries }) => {
            const id = entries[0]?.conversationId;
            return {
              ...acc,
              ...(id && { [id]: entries })
            };
          },
          {} as Record<string, ChatParticipant[]>
        );

        setParticipantsOfConversations(participantsOfConversations);
      });
    }
  }, [conversations, activity.private]);

  return {
    primaryConversation,
    otherConversations,
    participantsOfConversations
  };
};
